import React, { useState } from "react";
import { useAuth } from "context/react-lib/firebase-auth/AuthProvider";
import { useTheme } from "@emotion/react";
import { CircularProgress, IconButton, Skeleton, Stack } from "@mui/material";
import UserAvatar from "./UserAvatar";
import MarkdownBlock from "components/react-lib/markdown-code/MarkdownBlock";
import { fonts } from "fonts";
import { useDispatch } from "react-redux";
import { popupActions } from "store/react-lib/generic-ui/popup";
import GenerateAudioButtonV2 from "components/audio/GenerateAudioButtonV2";
import { db, functions } from "firebaseApp";
import { doc } from "firebase/firestore";
import TalkMessageMenu from "components/talk/TalkMessageMenu";
import ToolSelectorMenu from "components/bot/ToolSelectorMenu";
import { httpsCallable } from "firebase/functions";
import ToolResult from "components/talk/ToolResult";
import Message from "components/chat/Message";
import TranscribeIcon from "@mui/icons-material/Transcribe";

export default function TalkMessage({ message }) {
  const theme = useTheme();
  const auth = useAuth();
  const dispatch = useDispatch();

  const [processingTool, setProcessingTool] = useState(false);
  const [transcribing, setTranscribing] = useState(false);

  const applyTool = async (tool) => {
    try {
      setProcessingTool(true);
      const res = await httpsCallable(
        functions,
        "applyToolToTalkMessage"
      )({
        talkId: message.talkId,
        messageId: message.id,
        toolId: tool.id,
      });
      const resData = res.data;
      if (!resData.success) {
        dispatch(popupActions.showError(resData.error));
      }
    } catch (error) {
      console.log(error);
      dispatch(popupActions.showError("Something went wrong."));
    } finally {
      setProcessingTool(false);
    }
  };

  const handleTranscribe = async () => {
    try {
      setTranscribing(true);
      await httpsCallable(
        functions,
        "transcribeAudio"
      )({
        talkId: message.talkId,
        messageId: message.id,
      });
    } catch (error) {
      console.log(error);
      dispatch(popupActions.showError("Couldn't transcribe message."));
    } finally {
      setTranscribing(false);
    }
  };

  return (
    <>
      <div
        style={{
          backgroundColor:
            message.sender === auth.currentUser?.uid
              ? theme.palette.conversationBackground.default
              : theme.palette.conversationBackground.active,
          zIndex: 1,
          paddingTop: 20,
          paddingBottom: 10,
          paddingLeft: 16,
          paddingRight: 16,
          borderRadius: 8,
          position: "relative",
          width: "100%",
          boxSizing: "border-box",
          maxWidth: "748px",
        }}
      >
        <Stack spacing={1}>
          <Stack direction="row" justifyContent="space-between">
            <UserAvatar uid={message.sender} />
            <TalkMessageMenu message={message} />
          </Stack>
          {((transcribing && !message.content)) && (
            <Stack spacing={1}>
              <Skeleton variant="text" />
              <Skeleton variant="text" />
            </Stack>
          )}
          {message.content && (
            <MarkdownBlock
              markdownContent={message.content}
              canCopyCode={true}
              rawTextColor={theme.palette.text.main}
              rawCopyButtonColor={theme.palette.text.main}
              style={{
                color: theme.palette.text.main,
                lineHeight: 1.5,
                fontFamily: fonts.geist,
                overflowX: "auto",
              }}
              onCopy={() => dispatch(popupActions.showSuccess("Copied"))}
            />
          )}
          {message.audioUrl && (
            <audio
              style={{
                width: "100%",
                paddingTop: "10px",
              }}
              src={message.audioUrl}
              controls
            />
          )}
          <Stack direction="row">
            <ToolSelectorMenu
              onToolSelected={applyTool}
              processing={processingTool}
            />
            {!message.content && message.audioBase64 && (
              <IconButton onClick={handleTranscribe} disabled={transcribing}>
                {transcribing ? (
                  <CircularProgress size={24} color="secondary" />
                ) : (
                  <TranscribeIcon />
                )}
              </IconButton>
            )}
            {!message.audioUrl && (
              <GenerateAudioButtonV2
                text={message.content}
                docRef={doc(
                  db,
                  "talks",
                  message.talkId,
                  "messages",
                  message.id
                )}
              />
            )}
          </Stack>
        </Stack>
      </div>
      {message.toolResults &&
        [...message.toolResults]
          .sort((x, y) => x.createdAt - y.createdAt)
          .filter((toolResult) => toolResult.uid === auth.currentUser?.uid)
          .map((toolResult, index) => (
            <ToolResult
              key={`${message.id}-${index}`}
              toolResult={toolResult}
            />
          ))}
    </>
  );
}
